import { Link } from "react-router-dom";
import { ShieldCheck, Atom, Zap, Award } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function About() {
    const { t } = useTranslation();
    const commits = [
        { icon: Atom, key: "molecular" },
        { icon: ShieldCheck, key: "verified" },
        { icon: Zap, key: "coldChain" },
        { icon: Award, key: "coa" },
    ];
    const stats = ["founded", "compounds", "purity", "partners", "qc", "dispatch"];

    return (
        <div data-testid="about-page">
            <section className="relative max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 py-10 sm:py-16 lg:py-32">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-12 items-center">
                    <div className="lg:col-span-7">
                        <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-3">{t("about.eyebrow")}</div>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl xl:text-8xl font-extrabold uppercase tracking-tighter leading-[0.95]">
                            {t("about.titleLine1")}
                            <br />
                            {t("about.titleLine2")}
                            <br />
                            <span className="text-[#0077b6]">{t("about.titleAccent")}</span> {t("about.titleLine3")}
                        </h1>
                        <p className="mt-6 sm:mt-8 text-neutral-600 max-w-xl leading-relaxed text-sm sm:text-base lg:text-lg">
                            {t("about.intro")}
                        </p>
                    </div>
                    <div className="lg:col-span-5 relative">
                        <div className="aspect-[4/5] border border-neutral-200 overflow-hidden relative rounded-2xl">
                            <img
                                src="https://static.prod-images.emergentagent.com/jobs/d5c608c5-d491-4850-bad2-86a0f6155910/images/7c6c355a68885ffa06a11357ecda765a5c0089e4aa9d9c8447a336c2cba2b983.png"
                                alt="Partner lab"
                                className="w-full h-full object-cover"
                                style={{ filter: "grayscale(1) contrast(1.05) brightness(0.85)" }}
                            />
                            <div className="absolute inset-0 bg-[#f8f8f8] mix-blend-color opacity-80 pointer-events-none" />
                            <div className="absolute inset-0 bg-gradient-to-tr from-[#00aeef]/10 to-transparent pointer-events-none" />
                            <div className="absolute inset-0 bg-gradient-to-t from-white/60 via-transparent pointer-events-none" />
                        </div>
                    </div>
                </div>
            </section>

            <section className="border-y border-neutral-200 bg-white">
                <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 py-12 sm:py-20 lg:py-28">
                    <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-3">{t("about.standardsEyebrow")}</div>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl font-extrabold uppercase tracking-tighter mb-10 sm:mb-16">
                        {t("about.standardsTitleLine1")}
                        <br />
                        {t("about.standardsTitleLine2")}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 rounded-xl overflow-hidden">
                        {commits.map((c, i) => (
                            <div key={c.key} className="bg-[#ffffff] p-6 sm:p-8 lg:p-10" data-testid={`commit-${i}`}>
                                <c.icon className="w-6 h-6 text-[#0077b6] mb-5 sm:mb-6" />
                                <div className="font-display text-lg sm:text-xl font-bold mb-2 sm:mb-3">{t(`about.commit.${c.key}.t`)}</div>
                                <div className="text-sm text-neutral-500 leading-relaxed">{t(`about.commit.${c.key}.d`)}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 py-12 sm:py-20 lg:py-28">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-16">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-3">{t("about.missionEyebrow")}</div>
                        <h2 className="font-display text-2xl sm:text-3xl lg:text-5xl font-extrabold uppercase tracking-tighter mb-5 sm:mb-6">
                            {t("about.missionTitle")}
                        </h2>
                        <p className="text-neutral-600 leading-relaxed mb-4 text-sm sm:text-base">
                            {t("about.missionBody1")}
                        </p>
                        <p className="text-neutral-600 leading-relaxed text-sm sm:text-base">
                            {t("about.missionBody2")}
                        </p>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-3">{t("about.statsEyebrow")}</div>
                        <div className="grid grid-cols-2 gap-px bg-white/5 rounded-xl overflow-hidden">
                            {stats.map((key) => {
                                const [n, l] = t(`about.stats.${key}`, { returnObjects: true }) || ["", ""];
                                return (
                                    <div key={key} className="bg-white p-5 sm:p-8">
                                        <div className="font-display text-3xl sm:text-4xl font-extrabold text-[#0077b6] ">{n}</div>
                                        <div className="text-[10px] uppercase tracking-[0.18em] sm:tracking-[0.22em] text-neutral-500 mt-2">{l}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </section>

            <section className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 py-12 sm:py-16">
                <div className="border border-[#00aeef]/30 p-8 sm:p-10 lg:p-16 relative overflow-hidden">
                    <div className="absolute inset-0  opacity-20" />
                    <div className="relative">
                        <h2 className="font-display text-2xl sm:text-3xl lg:text-5xl font-extrabold uppercase tracking-tighter max-w-2xl">
                            {t("about.ctaTitle")}
                        </h2>
                        <Link to="/shop" className="inline-flex items-center gap-3 mt-6 sm:mt-8 bg-[#0089c8] hover:bg-[#0077b6] rounded-lg text-white font-bold uppercase tracking-[0.18em] text-xs px-5 sm:px-7 py-3.5 sm:py-4">
                            {t("about.ctaExplore")}
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
